/**
 * Change Control Page
 * 21 CFR Part 11 Compliance - Change Control Management
 *
 * Manages 4-stage workflow for all entity types:
 * DRAFT → UNDER REVIEW → APPROVED → IMPLEMENTED
 */

import React, { useState, useEffect } from 'react';
import {
  Tag,
  Button,
  Input,
  Select,
  Space,
  Card,
  Drawer,
  Descriptions,
  Divider,
  Tabs,
  Tooltip,
  Empty,
  Badge,
  Alert,
  Row,
  Col,
  Typography,
  Timeline,
  Statistic,
  Modal,
  Form,
  DatePicker,
} from 'antd';
import DataTable from '@/components/DataTable';
import {
  Search,
  RefreshCw,
  Download,
  GitBranch,
  FileText,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  AlertTriangle,
  Check,
  X,
  Plus,
  Filter,
  CheckCheck,
  Info,
  OctagonAlert,
  TriangleAlert,
  Minus,
  ArrowDown,
} from 'lucide-react';
import { IconLabel, OpenArrow, SummaryTiles, TONE } from '@/components/queue/QueueKit';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTheme } from '@/App';
import { formatDate, formatDateTime } from '@/utils/datetime';
import { defaultColors } from '@/themes/antd-theme';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Status configuration for color coding
 */
const getStatusConfig = (status) => {
  const configs = {
    DRAFT: {
      color: 'default',
      icon: <FileText size={14} />,
      text: 'Draft',
    },
    UNDER_REVIEW: {
      color: 'processing',
      icon: <Eye size={14} />,
      text: 'Under Review',
    },
    APPROVED: {
      color: 'success',
      icon: <CheckCircle size={14} />,
      text: 'Approved',
    },
    REJECTED: {
      color: 'error',
      icon: <XCircle size={14} />,
      text: 'Rejected',
    },
    IMPLEMENTED: {
      color: 'success',
      icon: <Check size={14} />,
      text: 'Implemented',
    },
  };
  return configs[status] || configs['DRAFT'];
};

/** Status / priority → icon + tone for the table (display only). */
const STATUS_ICON = {
  DRAFT: [FileText, TONE.slate],
  UNDER_REVIEW: [Eye, TONE.amber],
  APPROVED: [CheckCircle, TONE.green],
  REJECTED: [XCircle, TONE.red],
  IMPLEMENTED: [CheckCheck, TONE.green],
};
const PRIORITY_ICON = {
  CRITICAL: [OctagonAlert, TONE.red],
  HIGH: [TriangleAlert, TONE.amber],
  MEDIUM: [Minus, TONE.slate],
  LOW: [ArrowDown, TONE.slate],
};

/**
 * Priority configuration
 */
const getPriorityConfig = (priority) => {
  const configs = {
    CRITICAL: { color: 'red', text: 'Critical' },
    HIGH: { color: 'orange', text: 'High' },
    MEDIUM: { color: 'blue', text: 'Medium' },
    LOW: { color: 'green', text: 'Low' },
  };
  return configs[priority] || configs['MEDIUM'];
};

/**
 * Change Control Detail Drawer
 */
function ChangeControlDrawer({ visible, onClose, record, isDark }) {
  if (!record) return null;

  const statusConfig = getStatusConfig(record.workflow_status);

  // Dark mode colors for drawer
  const drawerBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const cardBgColor = isDark ? defaultColors.palette.gray[700] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  // Status card backgrounds for dark mode
  const getStatusCardBg = () => {
    if (isDark) {
      return statusConfig.color === 'success'
        ? defaultColors.palette.gray[700]
        : statusConfig.color === 'error'
        ? defaultColors.palette.gray[700]
        : statusConfig.color === 'processing'
        ? defaultColors.palette.gray[700]
        : defaultColors.palette.gray[700];
    }
    return statusConfig.color === 'success'
      ? '#f0fdf4'
      : statusConfig.color === 'error'
      ? '#fef2f2'
      : statusConfig.color === 'processing'
      ? '#eff6ff'
      : '#f9fafb';
  };

  return (
    <Drawer
      title={
        <div className="flex items-center gap-3">
          <GitBranch className="text-blue-600" size={24} />
          <div>
            <Title level={4} className="mb-0" style={{ color: textColor }}>
              {record.change_control_number}
            </Title>
            <Text style={{ color: secondaryTextColor }} className="text-xs">
              Change Control Details
            </Text>
          </div>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={720}
      zIndex={9999}
      closeIcon={<X size={20} style={{ color: textColor }} />}
      styles={{
        header: { backgroundColor: drawerBgColor, borderBottom: `1px solid ${borderColor}` },
        body: { backgroundColor: drawerBgColor },
      }}
    >
      <div className="space-y-6">
        {/* Status Card */}
        <Card
          className={`border-l-4 ${
            statusConfig.color === 'success'
              ? 'border-green-500'
              : statusConfig.color === 'error'
              ? 'border-red-500'
              : statusConfig.color === 'processing'
              ? 'border-blue-500'
              : 'border-gray-500'
          }`}
          style={{ backgroundColor: getStatusCardBg(), borderColor: borderColor }}
        >
          <div className="flex justify-between items-start">
            <div>
              <Text style={{ color: secondaryTextColor }} className="text-xs block mb-1">
                Current Status
              </Text>
              <Tag
                color={statusConfig.color}
                className="text-base font-semibold inline-flex items-center gap-1"
              >
                {statusConfig.icon}
                {statusConfig.text}
              </Tag>
            </div>
            <div>
              <Text style={{ color: secondaryTextColor }} className="text-xs block mb-1">
                Priority
              </Text>
              <Tag color={getPriorityConfig(record.priority).color} className="font-semibold">
                {getPriorityConfig(record.priority).text}
              </Tag>
            </div>
          </div>
        </Card>

        {/* Entity Information */}
        <div>
          <Title level={5} style={{ color: textColor }} className="mb-3">
            Entity Information
          </Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Entity Type">{record.entity_type_display}</Descriptions.Item>
            <Descriptions.Item label="Entity Name">{record.entity_name}</Descriptions.Item>
            <Descriptions.Item label="Change Type">{record.change_type_display}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Change Description */}
        <div>
          <Title level={5} style={{ color: textColor }} className="mb-3">
            Change Description
          </Title>
          <Card
            size="small"
            style={{
              backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
              borderColor,
            }}
          >
            <Paragraph style={{ color: textColor, marginBottom: 0 }}>
              {record.change_description}
            </Paragraph>
          </Card>
        </div>

        {/* Business Justification */}
        <div>
          <Title level={5} style={{ color: textColor }} className="mb-3">
            Business Justification
          </Title>
          <Card
            size="small"
            className="border-l-4 border-blue-400"
            style={{
              backgroundColor: isDark ? defaultColors.palette.gray[700] : '#eff6ff',
              borderColor,
            }}
          >
            <Paragraph style={{ color: textColor, marginBottom: 0 }}>
              {record.business_justification}
            </Paragraph>
          </Card>
        </div>

        {/* Impact Assessment */}
        {record.impact_assessment && (
          <div>
            <Title level={5} style={{ color: textColor }} className="mb-3">
              Impact Assessment
            </Title>
            <Card
              size="small"
              style={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#fefce8',
                borderColor,
              }}
            >
              <Paragraph style={{ color: textColor, marginBottom: 0 }}>
                {record.impact_assessment}
              </Paragraph>
            </Card>
          </div>
        )}

        {/* Risk Information */}
        <div>
          <Title level={5} style={{ color: textColor }} className="mb-3">
            Risk Assessment
          </Title>
          <Row gutter={16}>
            <Col span={12}>
              <Card size="small" style={{ backgroundColor: cardBgColor, borderColor }}>
                <Statistic
                  title={<span style={{ color: secondaryTextColor }}>Risk Level</span>}
                  value={record.risk_level_display}
                  prefix={<AlertTriangle size={16} />}
                  valueStyle={{
                    color:
                      record.risk_level === 'HIGH'
                        ? '#cf1322'
                        : record.risk_level === 'MEDIUM'
                        ? '#fa8c16'
                        : '#389e0d',
                  }}
                />
              </Card>
            </Col>
          </Row>
          {record.risk_mitigation && (
            <Card
              size="small"
              className="mt-3"
              style={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
                borderColor,
              }}
            >
              <Text strong className="block mb-2" style={{ color: textColor }}>
                Mitigation Strategy:
              </Text>
              <Paragraph style={{ color: textColor, marginBottom: 0 }}>
                {record.risk_mitigation}
              </Paragraph>
            </Card>
          )}
        </div>

        {/* Workflow Timeline */}
        <div>
          <Title level={5} style={{ color: textColor }} className="mb-3">
            Workflow Timeline
          </Title>
          <Timeline
            items={[
              record.initiated_by && {
                color: 'green',
                dot: <User size={16} />,
                children: (
                  <div>
                    <Text strong>Initiated</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.initiated_by} -{' '}
                      {formatDateTime(record.initiated_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                  </div>
                ),
              },
              record.submitted_for_review_by && {
                color: 'blue',
                dot: <Eye size={16} />,
                children: (
                  <div>
                    <Text strong>Submitted for Review</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.submitted_for_review_by} -{' '}
                      {formatDateTime(record.submitted_for_review_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                  </div>
                ),
              },
              record.reviewed_by && {
                color: 'orange',
                children: (
                  <div>
                    <Text strong>Reviewed</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.reviewed_by} - {formatDateTime(record.reviewed_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                    {record.review_comments && (
                      <Alert message={record.review_comments} type="info" className="mt-2" />
                    )}
                  </div>
                ),
              },
              record.approved_by && {
                color: 'green',
                dot: <CheckCircle size={16} />,
                children: (
                  <div>
                    <Text strong>Approved</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.approved_by} - {formatDateTime(record.approved_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                    {record.approval_comments && (
                      <Alert message={record.approval_comments} type="success" className="mt-2" />
                    )}
                  </div>
                ),
              },
              record.rejected_by && {
                color: 'red',
                dot: <XCircle size={16} />,
                children: (
                  <div>
                    <Text strong>Rejected</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.rejected_by} - {formatDateTime(record.rejected_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                    {record.rejection_reason && (
                      <Alert message={record.rejection_reason} type="error" className="mt-2" />
                    )}
                  </div>
                ),
              },
              record.implemented_by && {
                color: 'purple',
                dot: <Check size={16} />,
                children: (
                  <div>
                    <Text strong>Implemented</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      {record.implemented_by} -{' '}
                      {formatDateTime(record.implemented_at, 'YYYY-MM-DD HH:mm')}
                    </Text>
                    {record.implementation_notes && (
                      <Alert message={record.implementation_notes} type="info" className="mt-2" />
                    )}
                  </div>
                ),
              },
            ].filter(Boolean)}
          />
        </div>
      </div>
    </Drawer>
  );
}

/**
 * Main Change Control Page Component
 */
export default function ChangeControl() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // Dark mode colors
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  // Filters
  const [filters, setFilters] = useState({
    workflow_status: '',
    entity_type: '',
    priority: '',
    search: '',
  });

  // Statistics
  const [stats, setStats] = useState({
    by_status: [],
    total_count: 0,
    recent_count: 0,
  });

  /**
   * Fetch change controls - Mock data for now
   */
  const fetchData = async (page = 1, pageSize = 15) => {
    setLoading(true);

    // Mock data - replace with actual API call
    setTimeout(() => {
      const mockData = [
        {
          id: 1,
          change_control_number: 'CC-2024-0001',
          entity_type: 'STANDARD_BLOCK',
          entity_type_display: 'Standard Block',
          entity_name: 'Wet Granulation Process',
          change_type: 'MODIFICATION',
          change_type_display: 'Modification',
          workflow_status: 'UNDER_REVIEW',
          priority: 'HIGH',
          change_description: 'Updated temperature parameters for improved consistency',
          business_justification: 'Required for new product line requirements',
          initiated_by: 'John Doe',
          initiated_at: new Date().toISOString(),
          submitted_for_review_by: 'John Doe',
          submitted_for_review_at: new Date().toISOString(),
          risk_level: 'MEDIUM',
          risk_level_display: 'Medium Risk',
        },
        {
          id: 2,
          change_control_number: 'CC-2024-0002',
          entity_type: 'RECIPE',
          entity_type_display: 'Recipe',
          entity_name: 'Product-A Manufacturing Recipe',
          change_type: 'NEW',
          change_type_display: 'New Creation',
          workflow_status: 'DRAFT',
          priority: 'MEDIUM',
          change_description: 'New recipe for Product-A variant',
          business_justification: 'Market demand for new variant',
          initiated_by: 'Jane Smith',
          initiated_at: new Date().toISOString(),
          risk_level: 'LOW',
          risk_level_display: 'Low Risk',
        },
      ];

      setData(mockData);
      setPagination({
        current: page,
        pageSize,
        total: mockData.length,
      });
      setLoading(false);
    }, 500);
  };

  /**
   * Fetch statistics - Mock data
   */
  const fetchStats = () => {
    setStats({
      by_status: [
        { workflow_status: 'DRAFT', count: 5 },
        { workflow_status: 'UNDER_REVIEW', count: 3 },
        { workflow_status: 'APPROVED', count: 12 },
        { workflow_status: 'IMPLEMENTED', count: 8 },
      ],
      total_count: 28,
      recent_count: 8,
    });
  };

  // Load data on mount
  useEffect(() => {
    fetchData();
    fetchStats();
  }, []);

  // Handle row click
  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: 'Change control',
      dataIndex: 'change_control_number',
      key: 'cc_number',
      width: '28%',
      render: (text, record) => {
        const sub = [record.entity_name, record.entity_type_display].filter(Boolean).join(' · ');
        return (
          <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <Text strong className="font-mono">
              {text}
            </Text>
            <Tooltip title={sub}>
              <div
                style={{ color: secondaryTextColor, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {sub || '—'}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Change Type',
      dataIndex: 'change_type_display',
      key: 'change_type',
      width: '12%',
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'workflow_status',
      key: 'status',
      width: '12%',
      render: (status) => {
        const config = getStatusConfig(status);
        const [StatusIcon, color] = STATUS_ICON[status] || STATUS_ICON.DRAFT;
        return <IconLabel Icon={StatusIcon} color={color} label={config.text} />;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: '10%',
      render: (priority) => {
        const config = getPriorityConfig(priority);
        const [PriorityIcon, color] = PRIORITY_ICON[priority] || PRIORITY_ICON.MEDIUM;
        return <IconLabel Icon={PriorityIcon} color={color} label={config.text} title={`${config.text} priority`} />;
      },
    },
    {
      title: 'Initiated By',
      dataIndex: 'initiated_by',
      key: 'initiated_by',
      width: '12%',
    },
    {
      title: 'Date',
      dataIndex: 'initiated_at',
      key: 'date',
      width: '12%',
      render: (date) => formatDate(date),
    },
    {
      title: '',
      key: 'open',
      width: 56,
      align: 'center',
      render: (_, record) => <OpenArrow label="View details" onClick={() => handleRowClick(record)} />,
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBgColor }}>
      {/* Header */}
      <Card
        className="m-2.5 border border-gray-300 shadow-md"
        // bordered={false}
        style={{ backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.FileEdit className="text-blue-600" size={24} />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Change Control Management')}
            </Title>
            <Tooltip title="21 CFR Part 11 compliant change workflow: Draft → Under review → Approved → Implemented">
              <Info size={16} style={{ color: secondaryTextColor, cursor: 'help' }} aria-label="About change control" />
            </Tooltip>
          </div>
        }
      >
        {/* Statistics Cards */}
        <SummaryTiles
          testId="cc-summary"
          tiles={[
            { key: 'total', label: t('Total Changes'), value: stats.total_count, color: TONE.blue, Icon: GitBranch },
            {
              key: 'under_review',
              label: t('Under Review'),
              value: stats.by_status.find((s) => s.workflow_status === 'UNDER_REVIEW')?.count || 0,
              color: TONE.amber,
              Icon: Eye,
            },
            {
              key: 'approved',
              label: t('Approved'),
              value: stats.by_status.find((s) => s.workflow_status === 'APPROVED')?.count || 0,
              color: TONE.green,
              Icon: CheckCircle,
            },
            { key: 'recent', label: t('Recent (30 days)'), value: stats.recent_count, color: TONE.slate, Icon: Clock },
          ]}
        />

        {/* Filters */}
        <Card className="mb-4" style={{ backgroundColor: cardBgColor, borderColor }}>
          <Title level={5} className="mb-4" style={{ color: textColor }}>
            Filters
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Select
                className="w-full"
                placeholder="Status"
                value={filters.workflow_status}
                onChange={(value) => setFilters({ ...filters, workflow_status: value })}
                allowClear
              >
                <Option value="DRAFT">Draft</Option>
                <Option value="UNDER_REVIEW">Under Review</Option>
                <Option value="APPROVED">Approved</Option>
                <Option value="REJECTED">Rejected</Option>
                <Option value="IMPLEMENTED">Implemented</Option>
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Select
                className="w-full"
                placeholder="Entity Type"
                value={filters.entity_type}
                onChange={(value) => setFilters({ ...filters, entity_type: value })}
                allowClear
              >
                <Option value="STANDARD_BLOCK">Standard Block</Option>
                <Option value="RECIPE">Recipe</Option>
                <Option value="PRODUCT">Product</Option>
                <Option value="PROCESS">Process</Option>
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Select
                className="w-full"
                placeholder="Priority"
                value={filters.priority}
                onChange={(value) => setFilters({ ...filters, priority: value })}
                allowClear
              >
                <Option value="CRITICAL">Critical</Option>
                <Option value="HIGH">High</Option>
                <Option value="MEDIUM">Medium</Option>
                <Option value="LOW">Low</Option>
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Input
                placeholder="Search..."
                prefix={<Search size={14} />}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                allowClear
              />
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card style={{ backgroundColor: cardBgColor, borderColor }}>
          <DataTable
            height={null}
            columns={columns}
            data={data}
            loading={loading}
            pagination={pagination}
            onChange={(newPagination) => fetchData(newPagination.current, newPagination.pageSize)}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              className: `cursor-pointer ${isDark ? 'hover:bg-gray-700' : 'hover:bg-blue-50'}`,
            })}
            locale={{
              emptyText: <Empty description="No change controls found" />,
            }}
          />
        </Card>

        {/* Detail Drawer */}
        <ChangeControlDrawer
          visible={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          record={selectedRecord}
          isDark={isDark}
        />
      </Card>
    </div>
  );
}
